// ═══════════════════════════════════════
// MILBRANDS COWORK — Constants
// ═══════════════════════════════════════

export const ALLOWED_DOMAIN = 'milbrands.com.ar'

// Config de autenticación — se sobreescribe con configuracion/auth de Firestore.
// Gobierna qué MÉTODOS de login están disponibles (no quién elige: eso se decide
// en el alta admin o en el registro self-service). Legible SIN login (el login
// la lee antes de autenticar) → la regla de configuracion/auth es read público.
//   googleSSO: 'solo-milbrands' → Google ofrecido solo para el dominio staff (hd).
//              'todos'          → Google para cualquier dominio (sin hd).
//              'off'            → Google oculto; solo contraseña.
//   passwordEnabled: si el login por email+contraseña está disponible.
//   googleHd: dominio de Google Workspace para 'solo-milbrands' (configurable,
//             NO hardcodeado — default ALLOWED_DOMAIN por compat).
//   googleRegistro: si el AUTO-REGISTRO con Google se ofrece en /registro.
//             Desacoplado de googleSSO a propósito: permite abrir el LOGIN con
//             Google ('todos', sin hd) SIN exponer el alta self-service con
//             Google. Default false → registro con Google oculto hasta liberar.
export const AUTH_CONFIG_DEFAULT = {
  googleSSO: 'solo-milbrands',
  passwordEnabled: true,
  googleHd: ALLOWED_DOMAIN,
  googleRegistro: false,
}

// Branding default — se sobreescribe con configuracion/branding de Firestore
export const BRANDING_DEFAULT = {
  nombre: 'Milbrands',
  subtitulo: 'CoWork',
  nombreEquipo: 'Equipo MilBrands',  // label del segmento de profesionales asignados
  colorPrimario: null,    // usa CSS vars brand-accent
  colorSecundario: null,  // usa CSS vars brand-purple
}

// Firestore collection names
export const COLLECTIONS = {
  USUARIOS: 'usuarios',
  ACCESOS: 'accesos',
  ROLES: 'roles',
  ORGANIZACIONES: 'organizaciones',
  EMPRESAS: 'empresas',
  EMPRESAS_CONFIG_PROYECTOS: 'configProyectos',  // subcolección empresas/{id}/configProyectos — catálogos del módulo Proyectos (ADR 019)
  CUENTAS: 'cuentas',
  INTEGRACIONES: 'integraciones',
  FRAMEWORKS: 'frameworks',
  DIMENSIONES: 'dimensiones',
  CAPSULAS: 'capsulas',
  FOROS: 'foros',
  PROTOCOLOS: 'protocolos',
  REPORTES: 'reportes',
  ACCIONABLES: 'accionables',
  CONFIGURACION: 'configuracion',
  BIBLIOTECA: 'biblioteca',
  AI_PROMPTS: 'aiPrompts',
  AI_CONFIG: 'aiConfig',
  AI_CREDENTIALS: 'aiCredentials',
  FRAMEWORK_TIPOS: 'frameworkTipos',
  CONTACTOS: 'contactos',
  CATEGORIAS: 'categorias',
  TIPOS_NEGOCIO: 'tiposNegocio',
  EQUIPOS: 'equipos',
  LOCACIONES: 'locaciones',
  NIVELES_FRICCION: 'nivelesFriccion',
  ESTADOS_EMOCIONALES: 'estadosEmocionales',
  HORIZONTES_EXPECTATIVA: 'horizontesExpectativa',
  OBJETIVOS_CICLO: 'objetivosCiclo',
  CHECKPOINTS: 'checkpoints',
  TAMANOS_MERCADO: 'tamanosMercado',
  COMPETENCIAS: 'competencias',
  FORTALEZAS: 'fortalezas',
  DEBILIDADES: 'debilidades',
  TIPOS_REUNION: 'tiposReunion',
  REUNIONES: 'reuniones',
  REPORTES_EMPRESA: 'reportesEmpresa',
  REPORTES_GENERADOS: 'reportesGenerados', // PR-1a Cuadro General — fotos de dashboards publicadas al portal del seller
  WARNINGS: 'warnings',
  ESTADOS_NEGOCIO: 'estadosNegocio',
  OBJETIVOS_TRABAJO: 'objetivosTrabajo',
  CICLOS: 'ciclos',
  CANALES: 'canales',
  GLOSARIO: 'glosario',
  WARNING_CATEGORIAS: 'warningCategorias',
  CRITICALITY_FEEDBACK: 'criticalityFeedback',
  CRITICALITY_PREDICTIONS: 'criticalityPredictions',
  CRITICALITY_WEIGHTS: 'criticalityWeights',
  // Portal Cliente + Comunicaciones (Fase 1)
  CONVERSACIONES: 'conversaciones',   // Colección principal
  MENSAJES: 'mensajes',               // Subcolección bajo conversaciones/{id}/mensajes
  MENSAJES_PROGRAMADOS: 'mensajesProgramados', // Mensajes pendientes de envío (scheduler cada 5 min)
  DIFUSIONES: 'difusiones',           // Anuncios broadcast
  LECTURAS_DIFUSIONES: 'lecturasDifusiones',
  INVITACIONES: 'invitaciones',       // Códigos de invitación a portal
  AGENDA_EMPRESA: 'agendaEmpresa',     // LEGACY — se retira en Fase 6 (todo pasa a usuarios con invitado:false)
  // Arquitectura abril 2026 — permisos, mensajería y dinámicas
  MODULOS: 'modulos',                  // Registro auto-extensible de módulos del sistema
  ASIGNACIONES: 'asignaciones',        // Asignaciones explícitas (reemplaza jerarquía)
  TIPOS_ASIGNACION: 'tiposAsignacion', // ABM de tipos de relación en asignaciones
  SLAS: 'slas',                        // ABM de niveles de servicio (legacy — se depreca)
  SLA_INSTANCIAS: 'slaInstancias',     // Instancias vivas de SLAs aplicadas a recursos
  AGENTES: 'agentes',                   // Catálogo de agentes de automatización (1 por dashboard)
  // Subcollección: empresas/{empId}/agentesConfig → config de agente por empresa (activo, modo, destinatario)
  TIPOS_TAREA: 'tiposTarea',           // Catálogo de tipos de tarea con SLA default
  SLA_MENSAJERIA: 'slaMensajeria',     // Config global de SLA de respuesta a mensajes
  // Subcollección: empresas/{empId}/slaOverrides → overrides de SLA por empresa/usuario
  NOTIFICACIONES: 'notificaciones',    // Notificaciones por usuario (campana)
  AUDITORIA_EMPRESA_ESTADO: 'auditoriaEmpresaEstado', // Log de cambios de estado de empresa
  // Integración Google Calendar + Meet (abril 2026)
  // IMPORTANTE: REUNIONES (arriba) es la colección "Actividades" (registros
  // de reuniones ya tenidas, con transcripción y archivos). MEETS es la
  // colección nueva con el lifecycle de las reuniones agendadas (event/meet
  // link/estado) antes de que sucedan. Cuando termina y llega la transcripción,
  // se crea el doc en REUNIONES (Actividad) y se linkea con MEETS.
  MEETS: 'meets',
  GOOGLE_AUTH: 'googleAuth',           // Subcol bajo usuarios/{uid}/googleAuth/default
  ATAJOS_CHAT: 'atajosChat',           // ABM de shortcuts @reunion, @programar, etc.
  PLANES: 'planes',                     // ABM de planes (free, pro, premium, custom) con módulos incluidos
  VISTAS: 'vistas',                     // ABM de vistas/roles (power, coordinador, operador, asesor) — define UI + permisos
  // Catálogo de plataforma (Capa 0) — contenido modelo que MilBrands empuja a todas las empresas
  CATALOGO_PROBLEMATICAS: 'catalogoProblematicas',   // Problemáticas modelo (read-only para empresas)
  CATALOGO_FRAMEWORKS: 'catalogoFrameworks',         // Frameworks modelo (read-only para empresas)
  CATALOGO_ACCIONABLES: 'catalogoAccionables',       // Accionables modelo (read-only para empresas)
  CATALOGO_DIMENSIONES: 'catalogoDimensiones',       // Dimensiones modelo (read-only para empresas)
  CATALOGO_PROTOCOLOS: 'catalogoProtocolos',         // Protocolos modelo (read-only para empresas)
  CATALOGO_BIBLIOTECA: 'catalogoBiblioteca',         // Recursos de biblioteca modelo (read-only para empresas)
  // Base de Patrones (Plan IA Paso 6) — base única de patrones de negocio
  // cross-tenant: warnings + problemáticas (y futuros dominios), modelo y
  // propios de empresa, con flujo de curación propuesto→aprobado. Reemplaza
  // catalogoProblematicas + la colección warnings.
  BASE_PATRONES: 'basePatrones',
  // Cola de trabajos de user agents. DORMIDA: su worker era el Agente
  // Competencia, removido el 15-jun-2026 (cirugía §1.1, functions/index.js:221),
  // y su plomería de cliente (src/core/lib/colaAgente.js) salió del repo el
  // 10-sep-2026 por decisión de Mariano en el report 6AtU17Sqp6aE6Wy9EaSQ. El
  // nombre queda registrado porque la colección sigue existiendo: firestore.rules
  // la gatea y functions/lib/coberturaTenant.js la cubre en borrado y export por
  // tenant, así que los docs viejos siguen siendo del tenant que los creó.
  COLA_AGENTE: 'colaAgente',
  // ─── Protocolo Bombai — Fase 2a ───
  CONEXIONES_PLATAFORMAS: 'conexionesPlataformas',  // multi-cuenta multi-plataforma (eventualmente reemplaza mlConexiones)
  PROFESIONALES: 'profesionales',                   // consultores internos + marketplace futuro
  CONTRATOS: 'contratos',                           // profesional ↔ empresa
  HORAS: 'horas',                                   // tracking de horas trabajadas
  REVIEWS: 'reviews',                               // calificaciones de clientes a profesionales
  // ─── Protocolo Bombai — Fase 2c/2e ───
  TOKEN_CONEXION: 'tokenConexion',                  // self-service link multi-cuenta (TTL 7d, one-shot)
  AUDIT_LOGS: 'auditLogs',                          // registro inmutable de operaciones críticas
  // ─── Módulo Debugging (19-may-2026) — bug tracker interno para Q&A ───
  // Reportes con screenshot, contexto auto-capturado y comentarios. Subcolección
  // `comentarios` para hilo de ida/vuelta. Imágenes en Firebase Storage path
  // `/debugReports/{reportId}.png`.
  DEBUG_REPORTS: 'debugReports',
  // ─── Módulo Tutoriales (Bloque 1 — 26-may-2026) — capacitación interna ───
  // Videos cortos grabados in-app (pantalla + mic + cámara opcional, hasta 5 min)
  // mostrando cómo se hace algo en la plataforma. Archivo en Firebase Storage path
  // `tutoriales/{tutorialId}/video.webm`. Subcolecciones `comentarios` (hilo de
  // preguntas/feedback) y `vistas` (un doc por reproducción, para analytics).
  // Estados: publicado | pausado. Permisos editables: ver / grabar / eliminar.
  // Ver ESTADO_ACTUAL.md sesión 26-may para el plan completo.
  TUTORIALES: 'tutoriales',
  // ─── Protocolo Bombai — Fase 3 Módulo Productos ───
  MARCAS: 'marcas',                                 // marcas del catálogo de la empresa
  PROVEEDORES: 'proveedores',                       // proveedores de productos
  PRODUCTOS: 'productos',                           // catálogo comercial del seller (empresa-level)
  PRODUCTO_SOURCING: 'productoSourcing',            // sourcing producto↔proveedor con precio/moneda/MOQ
  PRODUCTO_SOURCING_PRECIO_HISTORIAL: 'productoSourcingPrecioHistorial',
  PRODUCTO_PRECIO_HISTORIAL: 'productoPrecioHistorial',  // historial unificado: compra/catalogo/plataforma
  // Bitácora de eventos por producto — alimenta cooldown de recomendaciones y
  // aprendizaje cross-empresa. Ver docs/bitacora-publicaciones.md.
  // Eventos: ad_activado / ad_desactivado / promo_activada / promo_desactivada /
  //          precio_subido / precio_bajado / contenido_actualizado / reposicion_stock /
  //          tarea_completada. Plan-gated por endpoint 'bitacora-publicaciones'.
  PRODUCTO_BITACORA: 'productoBitacora',
  CATALOGO_LINKS: 'catalogoLinks',                  // ⚠️ LEGACY — reemplazado por VINCULOS_PUBLICACION en el modelo canónico (Fase 1)
  // ─── Modelo canónico Fase 1 (27-may-2026) ────────────────────────────────
  // Reemplazan a `mlPublicaciones` y `catalogoLinks` respectivamente.
  // Shape canónico: núcleo común (español camelCase) + `datosCanal` (libre por canal).
  // Clave única lógica: (empresaId, canal, externalId). Doc id autogenerado.
  // Subcolecciones de publicaciones: `visitas` (serie diaria), `fullStock` (lotes Full).
  // Ver `docs/MODELO_CANONICO.md` (contrato completo).
  PUBLICACIONES: 'publicaciones',
  VINCULOS_PUBLICACION: 'vinculosPublicacion',
  ORDENES: 'ordenes',                                // canónica, reemplaza mlOrdenes (Fase 1)
  ENVIOS: 'envios',                                  // canónica, reemplaza mlEnvios (Fase 1, entidad propia)
  SYNC_JOBS: 'syncJobs',                             // jobs async de sync ML (Cloud Tasks pattern, Fase escalable)
  // Fase 3d — Catálogo Maestro a nivel plataforma (global, no per-empresa)
  CATALOGO_MAESTRO_MARCAS: 'catalogoMaestroMarcas',
  CATALOGO_MAESTRO_PRODUCTOS: 'catalogoMaestroProductos',
  // ─── Módulo Rentabilidad — Fase 1A ─────────────────────────────
  // Catálogos de "Costos por vender" por plataforma × país.
  // Cada plataforma×país es su propia colección. Ver CATALOGOS_COSTOS_POR_VENDER
  // abajo para el registro central de catálogos disponibles.
  CATALOGO_COSTOS_POR_VENDER_MLA: 'catalogoCostosPorVenderMLA',  // Mercado Libre Argentina
  CATALOGO_FIXED_FEE_MLA:         'catalogoFixedFeeMLA',          // Fixed fee ML por precio × peso × logistic_type
  // ─── Catálogos de Envíos por modalidad (PR Envíos MLA) ────────
  // 1 catálogo por modalidad porque cada una tiene schema distinto.
  // Todos siguen el mismo patrón: 1 doc vigente con arrays de tramos dentro.
  CATALOGO_ENVIOS_ME2_MLA:      'catalogoEnvioME2MLA',     // drop_off, cross_docking, xd_drop_off
  // ⚠️ catalogoEnvioFlexMLA NO es un duplicado de catalogoFlexMLA (D6, ADR
  // 050 §13 — verificado 19-jul): son DOS catálogos distintos con writers y
  // lectores propios. catalogoEnvioFlexMLA = TARIFARIO de envío Flex (seed
  // enviosMLA, tramos peso×precio). catalogoFlexMLA = escenarios canónicos
  // de COBERTURA Flex (flexMLA, coverage/zones). No se renombran (§13).
  CATALOGO_ENVIOS_FLEX_MLA:     'catalogoEnvioFlexMLA',    // self_service
  CATALOGO_ENVIOS_FULL_MLA:     'catalogoEnvioFullMLA',    // fulfillment (con inspección)
  CATALOGO_ENVIOS_TURBO_MLA:    'catalogoEnvioTurboMLA',   // turbo — próximamente (growth on-demand)
  CATALOGO_ENVIOS_ME1_MLA:      'catalogoEnvioME1MLA',     // default — próximamente
  CATALOGO_ENVIOS_CUSTOM_MLA:   'catalogoEnvioCustomMLA',  // custom — próximamente
  // ─── Catálogo plataforma de Impuestos por jurisdicción (Rentabilidad PR1) ─
  // Multi-país futuro. Hoy solo AR. Doc por jurisdicción con alícuotas default
  // (IVA, IIBB, Imp. Cred-Déb, factor ganancias). Aplica a TODAS las plataformas
  // (MLA, TN, Tienda Propia, Amazon) porque los impuestos son del país, no del canal.
  CATALOGO_IMPUESTOS:           'catalogoImpuestos',
  // Tipos de condición fiscal (RI, Monotributo, Exento). Cada uno con sus flags
  // de comportamiento (discrimina IVA, paga IIBB, etc.). Read-only para empresas.
  CATALOGO_CONDICIONES_FISCALES: 'catalogoCondicionesFiscales',
  // Override por empresa de los impuestos default. Selector de condición fiscal
  // + overrides numéricos opcionales (IIBB distinto por provincia, exenciones, etc.).
  EMPRESAS_IMPUESTOS:            'empresasImpuestos',
  // ⚠️ `CATEGORIAS` ya estaba declarada más arriba en este mismo objeto, con el
  // MISMO valor (report N7Bg2r1i2sqWx5iglWvn). La segunda pisaba a la primera
  // sin cambiar nada —por eso nunca se notó—, pero una clave declarada dos
  // veces en el mapa canónico de colecciones es una que el próximo puede
  // cambiar en un solo lugar creyendo que las cambió todas. El comentario de
  // dominio se conserva acá arriba, que es donde estaba.
  //
  // Categorías a nivel empresa (rubro del negocio del seller). Define el IVA que
  // se hereda al producto. Ver shape en `categorias` (Bombai PR Categorías).
  // → la entrada vive en la declaración de más arriba.
  // ─── Credenciales de apps MilBrands ante plataformas (PR5) ────
  // Una entrada por plataforma. Guarda client_id + client_secret encriptado
  // + app_token cacheado. Consumido por el backend para autenticar llamadas
  // a endpoints que requieren token de app (no de seller).
  PLATAFORMAS_CONFIG: 'plataformasConfig',
  // ─── Módulo Casos — Mayo 2026 ──────────────────────────────────
  // Entidad transversal: expedientes investigativos de días/semanas con
  // conversación, participantes internos+externos, vinculado a recursos y
  // tareas. Origen polimórfico (ML claims/returns, manual, regla, otra
  // plataforma, etc.). Ver docs/casos.md para spec completa.
  CASOS:                   'casos',                  // top-level con empresaId
  TIPOS_CASO:              'tiposCaso',              // ABM tipos de caso (paralelo a tiposTarea)
  CATEGORIAS_CASO:         'categoriasCaso',         // taxonomía cerrada admin (logistica, producto, etc.)
  // Subcolecciones bajo casos/{casoId}/:
  CASOS_FEED:              'feed',                   // case feed cronológico (comentarios + eventos + canales)
  CASOS_ADJUNTOS:          'adjuntos',               // archivos / evidencias del caso
  CASOS_APROBACIONES:      'aprobaciones',           // workflows de aprobación inline (si tipoCaso lo requiere)
  // ABM admin de macros y plantillas (top-level):
  MACROS_CASO:             'macrosCaso',             // secuencias de acciones aplicables con un click
  PLANTILLAS_RESPUESTA_CASO: 'plantillasRespuestaCaso', // text snippets reutilizables por tipo/categoría

  // ── Módulo Proyectos (jun-2026, ADR 018 + docs/modulo_proyectos.md) ──
  // "Nuestro Asana": orquestación de proyectos con hitos, equipo, entregables.
  PROYECTOS:               'proyectos',              // top-level con empresaId (canónica)
  ROADMAP_ITEMS:           'roadmapItems',           // top-level con empresaId — módulo Roadmap de desarrollo (multi-tenant)
  ROADMAP_TIPOS:           'roadmapTipos',           // top-level con empresaId — catálogo de tipos del Roadmap (ABM por empresa)
  ROADMAP_RESPONSABLES:    'roadmapResponsables',    // top-level con empresaId — catálogo de responsables (máquinas/agentes) del Roadmap (ABM por empresa)
  PROYECTOS_HITOS:         'hitos',                  // subcolección proyectos/{id}/hitos
  // Bloque 3 — entidad de tareas PROPIA del módulo (ADR 018 §2.2). NO es el
  // módulo Tareas/Asignaciones del sistema; es a medida para proyectos.
  TAREAS_PROYECTO:         'tareasProyecto',         // top-level con empresaId + proyectoId
  TAREAS_PROYECTO_HORAS:   'horasRegistradas',       // subcolección tareasProyecto/{id}/horasRegistradas (Bloque 4)
  PLANTILLAS_PROYECTO:     'plantillasProyecto',     // proyectos-tipo instanciables (Bloque 8)
  PROYECTOS_ARCHIVOS:      'archivos',               // subcolección proyectos/{id}/archivos — metadata de Storage (Bloque 6)
  PROYECTOS_ENTREGABLES:   'entregables',            // subcolección proyectos/{id}/hitos/{hitoId}/entregables (Bloque 13, diseño §4.2.1)
  PROYECTOS_POSTMORTEM:    'postMortem',             // subcolección proyectos/{id}/postMortem — doc único 'principal' (Bloque 17, §16)

  // ── Club Milbrands · Formación (21-ago-2026) ────────────────────────────
  // Contenido del OPERADOR del club, no de la empresa que lo consume: todas
  // llevan `operadorId`, ninguna lleva `empresaId`. Quién ve qué lo decide el
  // resolver de accesos (functions/accesos.js), no el dueño del dato.
  // El bloque es de la plataforma y se REUSA entre módulos; lo que cambia de un
  // módulo a otro (orden, sección —muere en T3b—, liberación) vive en MODULO_BLOQUES.
  INSTRUCTORES:            'instructores',           // ficha editorial de quien dicta (sin vínculo al usuario)
  BLOQUES:                 'bloques',                // pieza reutilizable: video + material (ex clases, rename 26-ago)
  BLOQUES_QUIZ:            'quiz',                   // subcolección bloques/{id}/quiz — legacy, una pregunta por doc
  CLUB_MODULOS:            'clubModulos',            // módulo (contenido del Club): armado de bloques (ex cursos, T3). `modulos` a secas YA es el registro de módulos de PLATAFORMA (:97) — el guard de claves duplicadas cazó la colisión.
  MODULO_BLOQUES:          'moduloBloques',          // subcolección modulos/{id}/moduloBloques/{bloqueId} — doc id = bloqueId
  VIDRIERAS:               'vidrieras',              // qué se destaca y entre qué fechas

}

// El operador de la PLATAFORMA: su contenido lo alcanza cualquier miembro cuyo
// plan incluya el módulo. ESPEJO EXACTO de `functions/accesos.js`, que es quien
// decide. El valor es un centinela y NO el nombre de una empresa — nació como
// `'milbrands'` y lo sacó el triage del adversarial: una marca dentro del
// resolver de accesos es lo que el proyecto prohíbe de plano, y el día que
// Milbrands exista como empresa cliente con su propio id, "operador milbrands"
// significaría dos cosas distintas en el mismo campo.
export const OPERADOR_PLATAFORMA = '__plataforma__'

// Niveles del bloque de Formación (26-ago, rediseño Bloques). Espejo de
// NIVELES en functions/club/contenido.js — paridad por test (niveles.parity).
export const NIVEL_INICIAL = 'inicial'
export const NIVEL_INTERMEDIO = 'intermedio'
export const NIVEL_AVANZADO = 'avanzado'

// (Acá vivían `AUDIENCIA_ABIERTA`/`AUDIENCIA_RESTRINGIDA`, espejo del mismo par
// en `functions/accesos.js`. Murieron con el campo el 31-ago — ADR
// `2026-08-31-muere-audiencia-el-club-gatea-solo-por-plan-y-grants`. Ojo al
// buscar: `audiencia` sigue vivo en REPORTES y en DIFUSIONES con otro
// vocabulario y otro significado; nada de esto los toca.)

// Tipos de usuario
// MilBrands es la única consultora. Consultores se crean manuales (admin).
// Clientes se pueden registrar solos vía /registro (Session 8).
export const USER_TIPOS = {
  MILBRANDS: 'milbrands',      // Equipo MilBrands (@milbrands.com.ar) — value canónico
  CONSULTOR: 'milbrands',      // Alias transicional (mismo valor) — se elimina en B9
  CLIENTE: 'cliente',          // Empresa cliente — puede auto-registrarse
  COLABORADOR: 'colaborador',  // Colaborador externo del cliente (freelance/contador/etc.)
}

// ── Tipos que EXISTEN en los datos, que no es lo mismo que el mapa de arriba ──
// (21-ago-2026, ADR identidad de persona del Club)
//
// `USER_TIPOS` son los valores que el código ESCRIBE. Esta lista son los que el
// código puede ENCONTRAR al leer un usuario, que incluye uno que ningún alias
// nombra: el literal `'consultor'`. Censo de staging del 21-ago sobre 24
// usuarios: 12 `milbrands`, 11 `cliente`, **1 `consultor`**, ninguno sin `tipo`.
// Ese `'consultor'` no sale de `USER_TIPOS.CONSULTOR` —que vale `'milbrands'`,
// es un alias— sino de datos viejos, y el gate de staff del backend ya lo tenía
// documentado ("es wizard con tipo:'consultor', NO tipo:'milbrands'" — censo C.1 t2).
//
// Para qué existe: el gate de visibilidad de módulos evalúa contra ESTA lista y
// **falla cerrado** ante un tipo ausente o desconocido. Antes hacía
// `(tipo || 'milbrands') === 'cliente'`, o sea que una cuenta sin tipo se trataba
// como staff y veía todo. Si la lista se armara con `USER_TIPOS`, el usuario
// `'consultor'` real quedaría afuera y perdería el acceso.
//
// ⚠️ Si se agrega un tipo nuevo (el candidato externo de la bolsa de empleo llega
// con el módulo `empleo`), va acá y en su espejo de `functions/`, que tiene test
// de paridad.
//
// ⚠️ `'colaborador'` NO está, a propósito (report eugkoJ8q7ivPyfXAj8Or).
// Lo resolvió el ADR 2026-09-29 (D7): un colaborador es un usuario `cliente`
// con un rol de colaborador, no un tipo. El servidor ya no deja nacer ese tipo
// (`TIPOS_USUARIO_DE_ALTA`, en el espejo de `functions/`) y los que quedan los
// migra el script de ese report (`migrar-tipo-colaborador-a-cliente`). Hasta
// que esa migración corra, un usuario con `'colaborador'` sigue rebotando en los
// dos gates que leen esta lista (el sidebar, `useModulosVisibles.js`, y el
// acceso a contenido del backend, `functions/accesos.js`): fallan cerrado.
export const TIPOS_USUARIO_CONOCIDOS = ['milbrands', 'consultor', 'cliente']

// Estados del ciclo de vida comercial de una empresa cliente
// Dispara hooks de acceso (ver PROYECTO_MilBrands_CoWork.md — Arquitectura abril 2026)
export const EMPRESA_ESTADOS = {
  ACTIVO: 'activo',                   // Normal — operando
  ASIGNADO: 'asignado',               // Recién asignado a PM/consultor
  STAND_BY: 'stand_by',               // Pausa temporal de servicio
  SUSPENSION: 'suspension',           // Falta de pago — usuarios del cliente pueden loguear pero no operar
  BAJA: 'baja',                       // Dados de baja — usuarios del cliente no pueden loguear
  BAJA_SIN_COBRO: 'baja_sin_cobro',   // Baja + tag contable
}

// Etiquetas legibles para UI
export const EMPRESA_ESTADO_LABELS = {
  activo: 'Activo',
  asignado: 'Asignado',
  stand_by: 'Stand by',
  suspension: 'Suspensión',
  baja: 'Baja',
  baja_sin_cobro: 'Baja sin cobro',
}

// Código de rol que se asigna temporalmente cuando la empresa entra en suspensión.
// Al volver a 'activo' se restaura desde usuarios.rolAnterior.
export const ROL_SUSPENDIDO_CODIGO = 'suspendido'

// Módulos del portal cliente (flags en contacto.portal)
export const PORTAL_MODULOS = {
  VER_EQUIPO: 'verEquipo',
  CHAT: 'chat',
  DOCUMENTOS: 'documentos',
  CICLOS: 'ciclos',
  APROBACIONES: 'aprobaciones',
}

// Tipos de conversación
export const CONVERSACION_TIPOS = {
  DIRECTA: 'directa',       // 1 a 1
  GRUPO: 'grupo',           // varios participantes, tema libre
  CONTEXTO: 'contexto',     // asociada a un plan/accionable/decision/etc
}

// Visibilidad de conversación
export const CONVERSACION_VISIBILIDAD = {
  PUBLICA: 'publica',       // Todos los participantes del contexto ven
  PRIVADA: 'privada',       // Solo invitados explícitos
}

// Estados / ciclo de vida de una conversación (cada "tema" se trata como un mini-ticket)
//   ENVIADO       → recién creada, nadie del otro lado lo abrió todavía
//   LEIDO         → al menos un destinatario lo abrió (auto al entrar al chat)
//   NOTIFICADO    → el receptor confirmó manualmente "vi y tomé nota" (sin respuesta)
//   EN_DISCUSION  → hay respuestas yendo y viniendo
//   CERRADO       → el creador (creadoPor) marcó el tema como resuelto
export const CONVERSACION_ESTADOS = {
  ENVIADO: 'enviado',
  LEIDO: 'leido',
  NOTIFICADO: 'notificado',
  EN_DISCUSION: 'en_discusion',
  CERRADO: 'cerrado',
}

// Tipos de mensaje
export const MENSAJE_TIPOS = {
  TEXTO: 'texto',
  ACCION: 'accion',        // embebido — pedido de aprobación, etc (futuro)
  SISTEMA: 'sistema',      // marcador automático (ej. "ciclo cerrado")
  REUNION_AGENDADA: 'reunion_agendada', // tarjeta con link de Meet (abril 2026)
  REUNION_CANCELADA: 'reunion_cancelada',
  REUNION_TRANSCRITA: 'reunion_transcrita', // cuando la transcripción llegó a Actividades
  REUNION_RSVP: 'reunion_rsvp',        // alguien aceptó o rechazó una reunión (abril 2026)
  REUNION_EDITADA: 'reunion_editada',  // organizador o invitado editó la reunión (abril 2026)
}

// Estados de RSVP para invitados a una reunión. Se guardan en /meets/{id}.rsvps[uid].
// - pending   → invitación creada, sin respuesta
// - accepted  → aceptó formalmente ("Sí voy")
// - declined  → rechazó ("No puedo")
// (La V1 NO incluye 'proposed' — si quieren negociar, lo hacen por mensaje libre.)
export const MEET_RSVP_ESTADOS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
}

// Prioridad / urgencia de mensajes (y de conversaciones como "piso" default).
// Comparte taxonomía con NOTIFICACION_PRIORIDADES para facilitar la propagación
// a notificaciones y SLAs (condiciones.prioridad).
//   baja      → FYI, sin apuro
//   normal    → default
//   alta      → requiere atención el mismo día
//   critica   → bloqueante / respuesta inmediata
export const MENSAJE_PRIORIDADES = {
  BAJA: 'baja',
  NORMAL: 'normal',
  ALTA: 'alta',
  CRITICA: 'critica',
}

export const MENSAJE_PRIORIDAD_LABELS = {
  baja:    'Baja',
  normal:  'Normal',
  alta:    'Alta',
  critica: 'Crítica',
}

// Color lateral del bubble / chip según prioridad efectiva (paleta semáforo).
//   baja    → verde    (calma)
//   normal  → azul     (por defecto)
//   alta    → ámbar    (alerta)
//   critica → rojo     (emergencia)
export const MENSAJE_PRIORIDAD_COLORES = {
  baja:    { borde: '#10B981', bg: 'rgba(16,185,129,0.15)',  fg: '#6EE7B7' },
  normal:  { borde: '#3B82F6', bg: 'rgba(59,130,246,0.12)',  fg: '#93C5FD' },
  alta:    { borde: '#F59E0B', bg: 'rgba(245,158,11,0.15)',  fg: '#FBBF24' },
  critica: { borde: '#EF4444', bg: 'rgba(239,68,68,0.18)',   fg: '#FCA5A5' },
}

export const MENSAJE_PRIORIDAD_DEFAULT = 'normal'

// Canales de venta por defecto (auto-seed)
export const CANALES_DEFAULT = [
  { id: 'mercado-libre', nombre: 'Mercado Libre', descripcion: 'Marketplace líder en LATAM', color: '#FFE600', icono: '🟡', orden: 1 },
  { id: 'tienda-nube', nombre: 'Tiendanube', descripcion: 'Plataforma de e-commerce propia', color: '#2563EB', icono: '☁️', orden: 2 },
  { id: 'tienda-propia', nombre: 'Tienda Propia', descripcion: 'E-commerce propio (WooCommerce, Shopify, etc.)', color: '#22C55E', icono: '🏪', orden: 3 },
  { id: 'amazon', nombre: 'Amazon', descripcion: 'Marketplace global', color: '#FF9900', icono: '📦', orden: 4 },
  { id: 'mayorista', nombre: 'Mayorista / B2B', descripcion: 'Venta mayorista o business-to-business', color: '#8B5CF6', icono: '🏭', orden: 5 },
  { id: 'retail', nombre: 'Retail / Punto de Venta', descripcion: 'Venta física o punto de venta propio', color: '#EC4899', icono: '🏬', orden: 6 },
]

// Tipos de recursos vinculables a protocolos
export const RECURSO_TIPOS_PROTOCOLO = [
  { id: 'herramienta', nombre: 'Herramienta', descripcion: 'Templates, planillas, checklists', icono: 'tool' },
  { id: 'reportes', nombre: 'Reportes', descripcion: 'Informes, reportes, datos necesarios', icono: 'bar-chart' },
  { id: 'capsula', nombre: 'Cápsula', descripcion: 'Videos de formación del Club MilBrands', icono: 'play-circle' },
  { id: 'foro', nombre: 'Foro', descripcion: 'Comunidad de discusión', icono: 'message-circle' },
  { id: 'marketplace', nombre: 'Marketplace', descripcion: 'Profesionales para contratar', icono: 'shopping-bag' },
]

// Configuración default de recursos habilitados
export const RECURSOS_HABILITADOS_DEFAULT = {
  herramienta: true,
  reportes: true,
  capsula: false,
  foro: false,
  marketplace: false,
}

// Tipos de media para la Biblioteca Multimedia
export const MEDIA_TIPOS = [
  { id: 'documento', nombre: 'Documento', descripcion: 'PDF, Excel, planillas, templates', icono: 'file-text', color: 'blue' },
  { id: 'video', nombre: 'Video', descripcion: 'Videos explicativos, tutoriales, walkthroughs', icono: 'play-circle', color: 'purple' },
  { id: 'link', nombre: 'Link', descripcion: 'URLs a herramientas, dashboards, recursos web', icono: 'external-link', color: 'cyan' },
  { id: 'imagen', nombre: 'Imagen', descripcion: 'Screenshots, diagramas, infografías', icono: 'image', color: 'green' },
]

// Permission levels
export const PERMISOS = {
  ADMIN: 'admin',
  EDITOR: 'editor',
  VIEWER: 'viewer',
}

// Resource types for access control
export const RECURSO_TIPOS = {
  SISTEMA: 'sistema',
  ORGANIZACION: 'organizacion',
  EMPRESA: 'empresa',
  CUENTA: 'cuenta',
}

// Default roles (seed data legacy — el seed v2 vive en scripts/seed-roles-v2.js).
// `permisoDefault` se removió: en v2 los permisos los define la matriz del rol.
export const ROLES_DEFAULT = [
  { nombre: 'Master Admin', color: '#E91E8C', orden: 1 },
  { nombre: 'Líder', color: '#3B82F6', orden: 2 },
  { nombre: 'Consultor', color: '#3B82F6', orden: 3 },
  { nombre: 'Dueño', color: '#9333EA', orden: 4 },
  { nombre: 'Gerente', color: '#9333EA', orden: 5 },
  { nombre: 'Empleado', color: '#F59E0B', orden: 6 },
  { nombre: 'Contador', color: '#F59E0B', orden: 7 },
  { nombre: 'Agencia de publicidad', color: '#06B6D4', orden: 8 },
  { nombre: 'Auditor', color: '#F59E0B', orden: 9 },
  { nombre: 'Viewer', color: '#71717A', orden: 10 },
]

// Framework types
export const FRAMEWORK_TIPOS = [
  { id: 'canal', nombre: 'Canal', descripcion: 'Plataforma o canal de venta' },
  { id: 'capacidad', nombre: 'Capacidad', descripcion: 'Capacidad habilitante del negocio' },
  { id: 'habilidad', nombre: 'Habilidad', descripcion: 'Habilidad de gestión o liderazgo' },
  { id: 'situacion', nombre: 'Situación', descripcion: 'Etapa del ciclo de vida del negocio en el canal' },
]

// Default frameworks (seed data)
export const FRAMEWORKS_DEFAULT = [
  { nombre: 'Mercado Libre', tipo: 'canal', color: '#FFE600', icono: 'shopping-cart', orden: 1, descripcion: 'Gestión integral del canal Mercado Libre: publicaciones, publicidad, reputación, logística.' },
  { nombre: 'Tienda Online', tipo: 'canal', color: '#3B82F6', icono: 'globe', orden: 2, descripcion: 'Sitio web propio: Tienda Nube, Shopify, WooCommerce. Tráfico, conversión, omnicanalidad.' },
  { nombre: 'Rentabilidad', tipo: 'capacidad', color: '#22C55E', icono: 'dollar-sign', orden: 3, descripcion: 'Conocimiento y control de márgenes, costos, pricing y simulación de escenarios.' },
  { nombre: 'Operaciones', tipo: 'capacidad', color: '#F59E0B', icono: 'settings', orden: 4, descripcion: 'Estructura operativa, procesos, despacho, stock, logística.' },
  { nombre: 'Gestión Comercial', tipo: 'capacidad', color: '#E91E8C', icono: 'trending-up', orden: 5, descripcion: 'Estrategia de ventas, catálogo, competencia, crecimiento.' },
  { nombre: 'Marca y Posicionamiento', tipo: 'capacidad', color: '#9333EA', icono: 'star', orden: 6, descripcion: 'Propuesta de valor, identidad de marca, diferenciación, comunicación.' },
  { nombre: 'Sistemas', tipo: 'capacidad', color: '#06B6D4', icono: 'cpu', orden: 7, descripcion: 'ERP, integraciones, automatización, sincronización multicanal.' },
  { nombre: 'Organización', tipo: 'capacidad', color: '#EF4444', icono: 'users', orden: 8, descripcion: 'Equipo, roles, capacitación, documentación de procesos, onboarding.' },
  { nombre: 'Dirección Estratégica', tipo: 'capacidad', color: '#8B5CF6', icono: 'compass', orden: 9, descripcion: 'Visión de largo plazo, diversificación, modelo de negocio, liderazgo.' },
]

// Modules
export const MODULOS = [
  { id: 'diagnostico', nombre: 'Diagnóstico' },
  { id: 'finanzas', nombre: 'Finanzas' },
  { id: 'productos', nombre: 'Productos' },
  { id: 'operaciones', nombre: 'Operaciones' },
  { id: 'publicidad', nombre: 'Publicidad' },
  { id: 'casos', nombre: 'Casos' },
]

// Categorías de empresa (seed data)
// Datos de mercado simulados por categoría — editables vía ABM
// tamanoMercadoId y competenciaId referencian IDs de las colecciones TAMANOS_MERCADO y COMPETENCIAS
// acosPromedio = costo publicidad / venta (%) típico de la categoría en MercadoLibre AR
export const CATEGORIAS_DEFAULT = [
  { nombre: 'Accesorios para Vehículos', tamanoMercadoId: 'mediano', competenciaId: 'atomizada', acosPromedio: 14 },
  { nombre: 'Agro', tamanoMercadoId: 'chico', competenciaId: 'mixta', acosPromedio: 10 },
  { nombre: 'Alimentos y Bebidas', tamanoMercadoId: 'grande', competenciaId: 'concentrada', acosPromedio: 12 },
  { nombre: 'Antigüedades y Colecciones', tamanoMercadoId: 'chico', competenciaId: 'atomizada', acosPromedio: 8 },
  { nombre: 'Arte/Librería/Mercería', tamanoMercadoId: 'chico', competenciaId: 'atomizada', acosPromedio: 10 },
  { nombre: 'Bebés', tamanoMercadoId: 'mediano', competenciaId: 'mixta', acosPromedio: 14 },
  { nombre: 'Belleza y Cuidado Personal', tamanoMercadoId: 'grande', competenciaId: 'mixta', acosPromedio: 15 },
  { nombre: 'Salud y Equipamiento Médico', tamanoMercadoId: 'mediano', competenciaId: 'mixta', acosPromedio: 13 },
  { nombre: 'Celulares y Teléfonos', tamanoMercadoId: 'grande', competenciaId: 'concentrada', acosPromedio: 18 },
  { nombre: 'Computación', tamanoMercadoId: 'grande', competenciaId: 'concentrada', acosPromedio: 17 },
  { nombre: 'Cámaras y Accesorios', tamanoMercadoId: 'chico', competenciaId: 'concentrada', acosPromedio: 14 },
  { nombre: 'Electrónica/Audio/Video', tamanoMercadoId: 'grande', competenciaId: 'concentrada', acosPromedio: 16 },
  { nombre: 'Electrodomésticos y Aires Acondicionados', tamanoMercadoId: 'grande', competenciaId: 'concentrada', acosPromedio: 14 },
  { nombre: 'Herramientas', tamanoMercadoId: 'mediano', competenciaId: 'mixta', acosPromedio: 12 },
  { nombre: 'Hogar/Muebles/Jardín', tamanoMercadoId: 'grande', competenciaId: 'mixta', acosPromedio: 13 },
  { nombre: 'Industrias y Oficinas', tamanoMercadoId: 'mediano', competenciaId: 'mixta', acosPromedio: 11 },
  { nombre: 'Inmuebles', tamanoMercadoId: 'mediano', competenciaId: 'atomizada', acosPromedio: 8 },
  { nombre: 'Instrumentos Musicales', tamanoMercadoId: 'chico', competenciaId: 'atomizada', acosPromedio: 11 },
  { nombre: 'Juegos y Juguetes', tamanoMercadoId: 'mediano', competenciaId: 'atomizada', acosPromedio: 13 },
  { nombre: 'Libros/Revistas/Cómics', tamanoMercadoId: 'chico', competenciaId: 'concentrada', acosPromedio: 9 },
  { nombre: 'Mascotas', tamanoMercadoId: 'mediano', competenciaId: 'mixta', acosPromedio: 13 },
  { nombre: 'Moda', tamanoMercadoId: 'grande', competenciaId: 'atomizada', acosPromedio: 16 },
  { nombre: 'Deportes y Fitness', tamanoMercadoId: 'mediano', competenciaId: 'mixta', acosPromedio: 14 },
  { nombre: 'Música/Películas/Series', tamanoMercadoId: 'chico', competenciaId: 'concentrada', acosPromedio: 10 },
  { nombre: 'Recuerdos/Cotillón/Fiesta', tamanoMercadoId: 'chico', competenciaId: 'atomizada', acosPromedio: 11 },
  { nombre: 'Servicios', tamanoMercadoId: 'chico', competenciaId: 'atomizada', acosPromedio: 9 },
  { nombre: 'Souvenirs/Tazas', tamanoMercadoId: 'chico', competenciaId: 'atomizada', acosPromedio: 10 },
  { nombre: 'Otras categorías', tamanoMercadoId: 'chico', competenciaId: 'atomizada', acosPromedio: 10 },
  { nombre: 'Para tu Negocio', tamanoMercadoId: 'mediano', competenciaId: 'mixta', acosPromedio: 12 },
]

// Tipos de negocio (seed data)
export const TIPOS_NEGOCIO_DEFAULT = [
  { id: 'fabricante', nombre: 'Fabricante', descripcion: 'Fabrica localmente' },
  { id: 'importador', nombre: 'Importador', descripcion: 'Importa productos' },
  { id: 'fabricante-importador', nombre: 'Fabricante/Importador', descripcion: 'Produce o importa su propia mercadería. Controla el producto desde el origen.' },
  { id: 'mayorista', nombre: 'Mayorista', descripcion: 'Compra a fabricantes/importadores y revende. Alto volumen, bajo margen unitario.' },
  { id: 'retail', nombre: 'Retail', descripcion: 'Vende al consumidor final. Puede tener marca propia o revender.' },
  { id: 'fabricante-retail', nombre: 'Fabricante + Retail', descripcion: 'Fabrica/importa y además vende directo al consumidor final.' },
  { id: 'mayorista-retail', nombre: 'Mayorista + Retail', descripcion: 'Compra al por mayor y también vende al consumidor final.' },
]

// Equipos - dimensión empresa (tamaño total de la empresa)
export const EQUIPOS_EMPRESA_DEFAULT = [
  { id: 'solo', nombre: 'Solo', descripcion: 'Unipersonal, hace todo', rango: '1 persona' },
  { id: 'micro', nombre: 'Micro', descripcion: 'Equipo mínimo, roles compartidos', rango: '2-5 personas' },
  { id: 'pequeno', nombre: 'Pequeño', descripcion: 'Roles definidos, estructura básica', rango: '6-15 personas' },
  { id: 'mediano', nombre: 'Mediano', descripcion: 'Áreas diferenciadas, mandos medios', rango: '16-50 personas' },
  { id: 'grande', nombre: 'Grande', descripcion: 'Estructura compleja, múltiples áreas', rango: '50+ personas' },
]

// Equipos - dimensión proyecto (personas asignadas al proyecto de consultoría)
export const EQUIPOS_PROYECTO_DEFAULT = [
  { id: 'proy-solo', nombre: 'Solo', descripcion: 'Una sola persona en el proyecto', rango: '1 persona' },
  { id: 'proy-micro', nombre: 'Micro', descripcion: 'Equipo mínimo de proyecto', rango: '2-3 personas' },
  { id: 'proy-pequeno', nombre: 'Pequeño', descripcion: 'Equipo de proyecto con roles definidos', rango: '4-6 personas' },
  { id: 'proy-mediano', nombre: 'Mediano', descripcion: 'Equipo de proyecto con áreas', rango: '7-12 personas' },
  { id: 'proy-grande', nombre: 'Grande', descripcion: 'Equipo de proyecto completo', rango: '13+ personas' },
]

// Locaciones (seed data)
export const LOCACIONES_DEFAULT = [
  { id: 'fabrica', nombre: 'Fábrica', descripcion: 'Planta de producción o manufactura' },
  { id: 'deposito', nombre: 'Depósito', descripcion: 'Almacenamiento y logística' },
  { id: 'showroom', nombre: 'Showroom', descripcion: 'Espacio de exhibición' },
  { id: 'local', nombre: 'Local', descripcion: 'Punto de venta al público' },
  { id: 'oficina', nombre: 'Oficina', descripcion: 'Espacio administrativo' },
  { id: 'home-office', nombre: 'Home Office', descripcion: 'Trabajo remoto desde casa' },
]

// Nivel de Fricción (variable blanda — diagnóstico)
export const NIVELES_FRICCION_DEFAULT = [
  { id: 'delegado', nombre: 'Delegado', descripcion: 'No puede ejecutar. Sin equipo, sin tiempo, sin conocimiento. El consultor hace todo.', orden: 1 },
  { id: 'asistido', nombre: 'Asistido', descripcion: 'Puede hacer algo con mucho soporte. Equipo mínimo. El consultor lidera.', orden: 2 },
  { id: 'guiado', nombre: 'Guiado', descripcion: 'Puede ejecutar con guía. Tiene algo de equipo y competencia básica.', orden: 3 },
  { id: 'orientado', nombre: 'Orientado', descripcion: 'Mayormente capaz. Tiene equipo, conocimiento, sistemas. Necesita estrategia.', orden: 4 },
  { id: 'autonomo', nombre: 'Autónomo', descripcion: 'Totalmente capaz. Equipo completo, competencia total. Puede ejecutar solo.', orden: 5 },
]

// Estado Emocional (variable blanda — comportamiento)
export const ESTADOS_EMOCIONALES_DEFAULT = [
  { id: 'desconectado', nombre: 'Desconectado', descripcion: 'No responde mensajes, cancela reuniones, no mira métricas. El negocio digital es un tema que evita.', orden: 1 },
  { id: 'agotado-resignado', nombre: 'Agotado/Resignado', descripcion: 'Responde pero sin energía. Acepta los problemas como inevitables. No pelea por mejorar.', orden: 2 },
  { id: 'frustrado-ansioso', nombre: 'Frustrado/Ansioso', descripcion: 'Tiene energía pero negativa. Quiere cambios ya. Puede ser reactivo, cambia de estrategia cada semana.', orden: 3 },
  { id: 'receptivo-abierto', nombre: 'Receptivo/Abierto', descripcion: 'Escucha, pregunta, acepta feedback. Está dispuesto a probar cosas nuevas. Confía en el proceso.', orden: 4 },
  { id: 'entusiasmado-proactivo', nombre: 'Entusiasmado/Proactivo', descripcion: 'Viene con ideas, mira sus métricas solo, propone acciones. Tiene energía positiva.', orden: 5 },
]

// Horizonte de Expectativa (variable blanda — timeline)
export const HORIZONTES_EXPECTATIVA_DEFAULT = [
  { id: 'inmediato', nombre: 'Inmediato', descripcion: '< 1 mes', orden: 1 },
  { id: 'corto-plazo', nombre: 'Corto plazo', descripcion: '1-6 meses', orden: 2 },
  { id: 'medio-plazo', nombre: 'Medio plazo', descripcion: '6-12 meses', orden: 3 },
  { id: 'largo-plazo', nombre: 'Largo plazo', descripcion: '12-18 meses', orden: 4 },
  { id: 'estrategico', nombre: 'Estratégico', descripcion: '18-24 meses', orden: 5 },
]

// Objetivo Principal (variable blanda — estrategia)
export const OBJETIVOS_CICLO_DEFAULT = [
  { id: 'sobrevivir', nombre: 'Sobrevivir', descripcion: 'Está perdiendo plata o facturación en caída. Necesita frenar la caída antes de crecer.', orden: 1 },
  { id: 'estabilizar', nombre: 'Estabilizar', descripcion: 'Tiene ventas pero inestables. Meses buenos y meses malos. Quiere un piso.', orden: 2 },
  { id: 'rentabilizar', nombre: 'Rentabilizar', descripcion: 'Vende pero no gana. Factura bien pero los márgenes son bajos o negativos.', orden: 3 },
  { id: 'crecer', nombre: 'Crecer', descripcion: 'Anda bien y quiere escalar. Busca más ventas, más productos, más canales.', orden: 4 },
  { id: 'dominar', nombre: 'Dominar', descripcion: 'Quiere ser líder de categoría. Construir marca referente. Visión ambiciosa.', orden: 5 },
]

export const TAMANOS_MERCADO_DEFAULT = [
  { id: 'chico', nombre: 'Chico', descripcion: '<$50M/mes, <500 ventas, pocos vendedores', orden: 1 },
  { id: 'mediano', nombre: 'Mediano', descripcion: '$50M-$500M/mes, 500-5.000 ventas', orden: 2 },
  { id: 'grande', nombre: 'Grande', descripcion: '>$500M/mes, +5.000 ventas, masivo', orden: 3 },
]

export const COMPETENCIAS_DEFAULT = [
  { id: 'atomizada', nombre: 'Atomizada', descripcion: 'Muchos chicos, nadie domina (top10 <30% share)', orden: 1 },
  { id: 'mixta', nombre: 'Mixta', descripcion: 'Todos los tamaños conviven (top10 30-60% share)', orden: 2 },
  { id: 'concentrada', nombre: 'Concentrada', descripcion: 'Pocos grandes controlan (top10 >60% share)', orden: 3 },
]

export const FORTALEZAS_DEFAULT = [
  { id: 'marca-propia', nombre: 'Marca propia / Producto propio', descripcion: 'Fabrica o tiene marca registrada. Dice "es nuestra marca", "somos fabricantes". Ves packaging propio.', orden: 1 },
  { id: 'fabrica-manufactura', nombre: 'Fábrica / Manufactura propia', descripcion: 'Produce su mercadería. Dice "tenemos fábrica", "la producción es nuestra". Controla costos de origen.', orden: 2 },
  { id: 'equipo-dedicado', nombre: 'Equipo dedicado (3+ personas)', descripcion: 'Hay un equipo asignado al canal. Dice "tenemos X personas", "hay alguien dedicado". Ves roles diferenciados.', orden: 3 },
  { id: 'experiencia-otros-canales', nombre: 'Experiencia en otros canales', descripcion: 'Ya vende por web, local físico o mayorista. Dice "tenemos tienda online", "vendemos por mayor". Entiende e-commerce.', orden: 4 },
  { id: 'erp-sistema', nombre: 'ERP / Sistema de gestión', descripcion: 'Tiene sistema para stock, facturación o gestión. Dice "usamos [ERP]", "tenemos sistema". Puede sincronizar con ML.', orden: 5 },
  { id: 'catalogo-amplio', nombre: 'Catálogo amplio / Stock disponible', descripcion: 'Tiene inventario listo. Dice "tenemos 300 SKUs", "stock no es problema". Sin quiebres frecuentes.', orden: 6 },
  { id: 'conocimiento-rubro', nombre: 'Conocimiento profundo del rubro', descripcion: 'Conoce su industria, proveedores, competencia offline. Dice "llevamos 20 años en esto". Sabe de producto.', orden: 7 },
  { id: 'capital-inversion', nombre: 'Capital / Capacidad de inversión', descripcion: 'Puede invertir en publicidad, fotos, logística. Dice "tenemos presupuesto", "no es tema de plata".', orden: 8 },
  { id: 'producto-competitivo-ml', nombre: 'Producto competitivo para ML', descripcion: 'El producto tiene demanda en ML, buen margen y es despachable. No es frágil ni sobredimensionado.', orden: 9 },
  { id: 'trayectoria-base-clientes', nombre: 'Trayectoria / Base de clientes', descripcion: 'Lleva años en el mercado. Dice "vendemos desde 2017", "25 años en el rubro". Tiene una reputación offline.', orden: 10 },
]

export const DEBILIDADES_DEFAULT = [
  { id: 'operador-solo', nombre: 'Operador solo / Sin equipo', descripcion: 'El dueño hace todo: publica, despacha, responde. Dice "soy yo solo", "somos 2 pero hacemos todo nosotros".', orden: 1 },
  { id: 'sin-conocimiento-ml', nombre: 'Sin conocimiento de ML', descripcion: 'No sabe cómo funciona ML. Dice "¿qué es ACOS?", "no sé qué es matriz de producto", "no entiendo la plataforma".', orden: 2 },
  { id: 'no-mide-sin-datos', nombre: 'No mide / Sin datos', descripcion: 'Sin métricas. Dice "no sé cuánto vendo exactamente", "no tengo tablero", "nunca miré los datos de ML".', orden: 3 },
  { id: 'sin-publicidad', nombre: 'Sin publicidad / No invierte en pauta', descripcion: 'Inversión $0 en Product Ads. Dice "nunca pauté", "le tengo miedo a gastar", "la dejé en automático".', orden: 4 },
  { id: 'procesos-manuales', nombre: 'Procesos manuales / Sin sistematización', descripcion: 'Todo a mano: stock en Excel, precios a ojo. Dice "lo controlo con planilla", "voy viendo", "no tenemos proceso".', orden: 5 },
  { id: 'dueno-hace-todo', nombre: 'Dueño hace todo / Sin delegación', descripcion: 'El dueño es el cuello de botella. Dice "yo me encargo de todo", "si no estoy yo se para". Cancela reuniones.', orden: 6 },
  { id: 'sin-estrategia-catalogo', nombre: 'Sin estrategia de catálogo', descripcion: 'Publica todo sin criterio. Dice "subí todo lo que tengo", "tengo 300 publicaciones pero vendo 20".', orden: 7 },
  { id: 'no-conoce-rentabilidad', nombre: 'No conoce su rentabilidad', descripcion: 'No sabe cuánto gana. Dice "no sé cuánto me queda", "le pongo un 40%" sin descontar comisiones ni envío.', orden: 8 },
  { id: 'logistica-limitada', nombre: 'Logística limitada / Lejos de centros', descripcion: 'No puede hacer Full/Flex. Dice "estoy lejos de Buenos Aires", "no puedo hacer Full", "los envíos me matan".', orden: 9 },
  { id: 'sin-marca-genericos', nombre: 'Sin marca / Productos genéricos', descripcion: 'Revende sin diferenciación. Dice "vendemos lo mismo que todos", "somos revendedores". Compite solo por precio.', orden: 10 },
  { id: 'contenido-pobre', nombre: 'Contenido pobre / Publicaciones débiles', descripcion: 'Fotos de celular, títulos copiados, fichas vacías. Dice "las fotos las saqué yo", "no sé qué poner en la ficha".', orden: 11 },
  { id: 'resistencia-cambio', nombre: 'Resistencia al cambio / Rigidez', descripcion: 'No quiere cambiar. Dice "siempre hicimos así", "eso no funciona en mi rubro". Cuestiona cada recomendación.', orden: 12 },
]

// Categorías de Warnings
export const WARNING_CATEGORIAS = [
  { id: 'economico', nombre: 'Económico', color: '#F59E0B', icono: 'dollar', descripcion: 'Facturación, pagos, costos, rentabilidad' },
  { id: 'operativo', nombre: 'Operativo', color: '#EF4444', icono: 'settings', descripcion: 'Seguimiento, gestión, campañas, stock, bloqueos' },
  { id: 'comunicacional', nombre: 'Comunicacional', color: '#3B82F6', icono: 'message', descripcion: 'Falta de respuesta, comunicación, seguimiento' },
  { id: 'estrategico', nombre: 'Estratégico', color: '#8B5CF6', icono: 'compass', descripcion: 'Plan, resultados, alineación de expectativas' },
  { id: 'emocional', nombre: 'Emocional', color: '#E91E8C', icono: 'heart', descripcion: 'Frustración, enojo, malestar, desconfianza' },
]

// Warnings seed data (extraídos del análisis de 440 warnings reales)
export const WARNINGS_DEFAULT = [
  // Económico
  { id: 'caida-ventas', nombre: 'Caída de ventas', categoria: 'economico', severidad: 4, descripcion: 'El cliente reporta baja significativa en ventas respecto a períodos anteriores.', keywords: 'ventas bajaron, caída, se redujo, menos ventas, facturación cayó', orden: 1 },
  { id: 'no-puede-pagar', nombre: 'Dificultad de pago', categoria: 'economico', severidad: 5, descripcion: 'El cliente expresa dificultad para pagar el servicio o la factura.', keywords: 'no puedo pagar, presupuesto, caro, ajustar costos, reducir', orden: 2 },
  { id: 'no-ve-roi', nombre: 'No percibe retorno', categoria: 'economico', severidad: 4, descripcion: 'El cliente siente que la inversión en consultoría no se traduce en resultados económicos.', keywords: 'no veo resultados, no rinde, no se justifica, inversión, retorno', orden: 3 },
  { id: 'costos-altos', nombre: 'Costos operativos altos', categoria: 'economico', severidad: 3, descripcion: 'Los costos de operación (envíos, comisiones, publicidad) están erosionando el margen.', keywords: 'comisiones, envío me mata, costos altos, no queda margen', orden: 4 },
  // Operativo
  { id: 'falta-seguimiento', nombre: 'Falta de seguimiento del PM', categoria: 'operativo', severidad: 5, descripcion: 'El cliente percibe que el project manager no hace seguimiento activo.', keywords: 'no me contactan, sin seguimiento, abandonado, nadie me llama', orden: 5 },
  { id: 'problemas-internos', nombre: 'Problemas internos del cliente', categoria: 'operativo', severidad: 3, descripcion: 'El cliente tiene problemas internos que afectan la ejecución (equipo, sistemas, tiempo).', keywords: 'no tengo tiempo, se fue un empleado, problemas internos, no puedo', orden: 6 },
  { id: 'bloqueo-cuenta', nombre: 'Cuenta bloqueada/suspendida', categoria: 'operativo', severidad: 5, descripcion: 'La cuenta del cliente en la plataforma está bloqueada o suspendida.', keywords: 'me bloquearon, cuenta suspendida, bloqueada, no puedo publicar', orden: 7 },
  { id: 'sin-accion-visible', nombre: 'Sin acción visible', categoria: 'operativo', severidad: 4, descripcion: 'El cliente no percibe acciones concretas o cambios realizados por el equipo.', keywords: 'no hicieron nada, no veo cambios, qué se hizo, sin avances', orden: 8 },
  // Comunicacional
  { id: 'no-responde-cliente', nombre: 'Cliente no responde', categoria: 'comunicacional', severidad: 3, descripcion: 'El cliente dejó de responder mensajes, mails o llamadas.', keywords: 'no responde, no contesta, ghosteó, sin respuesta', orden: 9 },
  { id: 'no-responde-pm', nombre: 'PM no responde al cliente', categoria: 'comunicacional', severidad: 5, descripcion: 'El cliente reclama que el PM no responde sus consultas o mensajes.', keywords: 'no me responden, mando mails y nada, sin respuesta del equipo', orden: 10 },
  { id: 'expectativas-desalineadas', nombre: 'Expectativas desalineadas', categoria: 'comunicacional', severidad: 4, descripcion: 'El cliente esperaba algo diferente del servicio o los resultados prometidos.', keywords: 'no es lo que esperaba, me dijeron que, prometieron, diferente', orden: 11 },
  { id: 'pide-baja', nombre: 'Solicita baja del servicio', categoria: 'comunicacional', severidad: 5, descripcion: 'El cliente expresó intención de dar de baja el servicio de consultoría.', keywords: 'quiero la baja, dar de baja, cancelar, último mes, no sigo', orden: 12 },
  // Estratégico
  { id: 'sin-plan-claro', nombre: 'Sin plan claro', categoria: 'estrategico', severidad: 3, descripcion: 'El cliente siente que no hay un plan de trabajo definido o comunicado.', keywords: 'no sé el plan, qué sigue, sin estrategia, no hay rumbo', orden: 13 },
  { id: 'resultados-lentos', nombre: 'Resultados demasiado lentos', categoria: 'estrategico', severidad: 3, descripcion: 'El cliente percibe que los avances son demasiado lentos para lo que esperaba.', keywords: 'muy lento, cuándo, tarda mucho, esperaba más rápido', orden: 14 },
  { id: 'cambio-direccion', nombre: 'Cambio de dirección del negocio', categoria: 'estrategico', severidad: 3, descripcion: 'El cliente está repensando su modelo de negocio o cambiando de rumbo.', keywords: 'vamos a cambiar, repensar, otra dirección, pivotar', orden: 15 },
  // Emocional
  { id: 'frustrado-servicio', nombre: 'Frustración con el servicio', categoria: 'emocional', severidad: 4, descripcion: 'El cliente expresa frustración directa con el servicio de consultoría.', keywords: 'frustrado, harto, cansado, no aguanto más, decepcionado', orden: 16 },
  { id: 'enojo-plataforma', nombre: 'Enojo con la plataforma', categoria: 'emocional', severidad: 2, descripcion: 'El cliente está enojado con Mercado Libre u otra plataforma, no con la consultora.', keywords: 'mercado libre me tiene, la plataforma, cambian las reglas, injusto', orden: 17 },
  { id: 'desconfianza', nombre: 'Pérdida de confianza', categoria: 'emocional', severidad: 5, descripcion: 'El cliente expresa desconfianza hacia el equipo o el servicio.', keywords: 'no confío, no creo que, dudas, no estoy seguro de seguir', orden: 18 },
  { id: 'agotamiento', nombre: 'Agotamiento general', categoria: 'emocional', severidad: 3, descripcion: 'El cliente muestra señales de cansancio y agotamiento con su negocio.', keywords: 'estoy cansado, agotado, no doy más, quemado, saturado', orden: 19 },
  // ═══ Problemáticas (problemas estructurales diagnosticados) ═══
  { id: 'sin-analisis-rentabilidad', nombre: 'Falta de análisis de rentabilidad por producto', tipo: 'problematica', categoria: 'economico', severidad: 4, descripcion: 'No se analizan márgenes ni rentabilidad por producto, línea o canal. Las decisiones de pricing se toman sin datos.', keywords: 'margen, rentabilidad, pricing, no sabe cuánto gana, costo por producto', recomendacion: 'Implementar planilla de costos y márgenes por producto/categoría.', orden: 20 },
  { id: 'sin-gestion-stock', nombre: 'Falta de sistema de gestión de stock', tipo: 'problematica', categoria: 'operativo', severidad: 4, descripcion: 'No tiene sistema para controlar inventario. Roturas de stock frecuentes o sobrestock.', keywords: 'stock, inventario, no sé cuánto tengo, se quedó sin, quiebre', recomendacion: 'Evaluar herramientas de gestión de stock según escala del negocio.', orden: 21 },
  { id: 'sin-estrategia-pricing', nombre: 'Sin estrategia de pricing', tipo: 'problematica', categoria: 'estrategico', severidad: 4, descripcion: 'Los precios se definen sin criterio claro, sin considerar competencia, costos reales o posicionamiento.', keywords: 'precio, caro, barato, no sé cuánto cobrar, competencia cobra menos', recomendacion: 'Definir estrategia de pricing basada en costos + competencia + posicionamiento.', orden: 22 },
  { id: 'logistica-ineficiente', nombre: 'Logística ineficiente', tipo: 'problematica', categoria: 'operativo', severidad: 3, descripcion: 'Problemas recurrentes con envíos, tiempos de preparación, devoluciones o costos logísticos.', keywords: 'envío, demora, devolución, logística, fulfillment, preparación', recomendacion: 'Revisar proceso logístico end-to-end y evaluar alternativas (fulfillment, flex).', orden: 23 },
  { id: 'catalogo-desordenado', nombre: 'Catálogo desordenado o incompleto', tipo: 'problematica', categoria: 'operativo', severidad: 3, descripcion: 'Publicaciones mal armadas, sin fotos de calidad, títulos incorrectos, fichas incompletas.', keywords: 'publicaciones, fotos, títulos, fichas, catálogo, desordenado', recomendacion: 'Plan de optimización de catálogo: fichas técnicas, fotos, títulos SEO.', orden: 24 },
  { id: 'sin-metricas', nombre: 'No mide métricas clave', tipo: 'problematica', categoria: 'estrategico', severidad: 3, descripcion: 'No trackea KPIs básicos: conversión, ticket promedio, costo de adquisición, margen.', keywords: 'métricas, datos, no sabe, conversión, ticket promedio, kpi', recomendacion: 'Definir dashboard mínimo con KPIs relevantes para el negocio.', orden: 25 },
  { id: 'dependencia-un-canal', nombre: 'Dependencia de un solo canal', tipo: 'problematica', categoria: 'estrategico', severidad: 3, descripcion: 'Todo el negocio depende de un solo marketplace o canal. Alto riesgo ante cambios de plataforma.', keywords: 'solo mercado libre, un canal, diversificar, riesgo, dependencia', recomendacion: 'Planificar estrategia multicanal gradual.', orden: 26 },
  { id: 'sin-equipo-ecommerce', nombre: 'Sin equipo dedicado a e-commerce', tipo: 'problematica', categoria: 'operativo', severidad: 3, descripcion: 'El cliente maneja todo solo o con personal no especializado. No hay quien ejecute la estrategia.', keywords: 'solo, no tengo equipo, hace todo él, no da abasto, recurso humano', recomendacion: 'Evaluar incorporación de perfil dedicado o tercerización parcial.', orden: 27 },
  { id: 'atencion-cliente-deficiente', nombre: 'Atención al cliente deficiente', tipo: 'problematica', categoria: 'operativo', severidad: 3, descripcion: 'Demoras en responder preguntas, reclamos sin resolver, baja reputación por mala atención.', keywords: 'preguntas, reclamos, reputación, demora en responder, atención', recomendacion: 'Implementar protocolo de atención con tiempos de respuesta definidos.', orden: 28 },
  { id: 'sin-estrategia-publicidad', nombre: 'Sin estrategia de publicidad', tipo: 'problematica', categoria: 'estrategico', severidad: 3, descripcion: 'No invierte en publicidad o lo hace sin criterio. No mide ROAS ni optimiza campañas.', keywords: 'publicidad, product ads, campaña, roas, no invierte, gasto', recomendacion: 'Diseñar estrategia de inversión publicitaria con métricas de retorno.', orden: 29 },
  { id: 'facturacion-informal', nombre: 'Facturación informal o desordenada', tipo: 'problematica', categoria: 'operativo', severidad: 4, descripcion: 'No factura correctamente, tiene problemas impositivos o no lleva registros formales.', keywords: 'factura, impuestos, monotributo, iva, afip, informal, contador', recomendacion: 'Ordenar situación fiscal y proceso de facturación.', orden: 30 },
  { id: 'marca-sin-identidad', nombre: 'Marca sin identidad definida', tipo: 'problematica', categoria: 'estrategico', severidad: 2, descripcion: 'No tiene identidad de marca clara. Sin diferenciación visual ni propuesta de valor definida.', keywords: 'marca, branding, identidad, logo, diferenciación, propuesta de valor', recomendacion: 'Trabajar identidad de marca y propuesta de valor diferencial.', orden: 31 },
]

// Acciones de IA disponibles para tipos de reunión
export const AI_ACCIONES_REUNION = [
  { id: 'checkpoint', nombre: 'Completar Checkpoint', descripcion: 'Analiza la transcripción y pre-completa el checkpoint de diagnóstico' },
  { id: 'emocional', nombre: 'Análisis Emocional', descripcion: 'Detecta estado emocional del cliente y señales de alerta' },
  { id: 'problematicas', nombre: 'Problemáticas', descripcion: 'Identifica problemáticas y warnings en la transcripción' },
  { id: 'coherencia', nombre: 'Coherencia con Ciclo', descripcion: 'Evalúa alineación de la reunión con el plan del ciclo activo' },
  { id: 'tareas', nombre: 'Detección de Tareas', descripcion: 'Detecta tareas para el equipo consultor y el cliente, las crea como pendientes de revisión' },
]

export const TIPOS_REUNION_DEFAULT = [
  { id: 'bienvenida', nombre: 'Bienvenida', descripcion: 'Primera reunión de presentación y onboarding', orden: 1, aiAcciones: ['checkpoint', 'emocional', 'problematicas', 'tareas'], autoEjecutarIA: true },
  { id: 'kick-off', nombre: 'Kick Off', descripcion: 'Arranque formal del proyecto de consultoría', orden: 2, aiAcciones: ['tareas'], autoEjecutarIA: true },
  { id: 'seguimiento', nombre: 'Seguimiento', descripcion: 'Reunión periódica de revisión y avance', orden: 3, aiAcciones: ['checkpoint', 'emocional', 'problematicas', 'coherencia', 'tareas'], autoEjecutarIA: true },
  { id: 'equipo-milbrands', nombre: 'Reunión de Equipo Milbrands', descripcion: 'Reunión interna del equipo — solo detecta tareas', orden: 4, aiAcciones: ['tareas'], autoEjecutarIA: true },
]

// Account types (cuentas)
export const TIPOS_CUENTA = [
  { id: 'mercadolibre', nombre: 'Mercado Libre' },
  { id: 'tienda_online', nombre: 'Tienda Online' },
  { id: 'sitio_web', nombre: 'Sitio Web' },
  { id: 'instagram', nombre: 'Instagram' },
  { id: 'facebook', nombre: 'Facebook' },
  { id: 'otro', nombre: 'Otro' },
]

// ═══ Accionables: ejecutores, esfuerzo, tipo, impacto ═══
export const EJECUCION_ROLES = [
  { id: 'consultora', nombre: 'Consultora', color: 'cyan', icon: '🏢' },
  { id: 'cliente', nombre: 'Cliente', color: 'amber', icon: '👤' },
]

export const ESFUERZO_NIVELES = [
  { id: 'bajo', nombre: 'Bajo', color: 'emerald', icon: '●' },
  { id: 'medio', nombre: 'Medio', color: 'yellow', icon: '●●' },
  { id: 'alto', nombre: 'Alto', color: 'red', icon: '●●●' },
]

export const TIPO_FRECUENCIA = [
  { id: 'unica_vez', nombre: 'Única vez', color: 'zinc', descripcion: 'Se hace una sola vez' },
  { id: 'eventual', nombre: 'Eventual', color: 'blue', descripcion: 'Se hace cuando surge la necesidad' },
  { id: 'continuo', nombre: 'Continuo', color: 'purple', descripcion: 'Se hace de forma recurrente' },
]

export const IMPACTO_NIVELES = [
  { id: 'bajo', nombre: 'Bajo', color: 'zinc' },
  { id: 'medio', nombre: 'Medio', color: 'yellow' },
  { id: 'alto', nombre: 'Alto', color: 'emerald' },
]

// ═══ Estados de Negocio (en qué momento está el cliente) ═══
export const ESTADOS_NEGOCIO_DEFAULT = [
  { id: 'ingreso-canal', nombre: 'Ingreso al canal', descripcion: 'El cliente recién entra al canal de venta (marketplace, e-commerce). Está arrancando de cero.', color: '#3B82F6', orden: 1 },
  { id: 'activacion', nombre: 'Activación', descripcion: 'Ya tiene cuenta y publicaciones pero necesita generar las primeras ventas consistentes.', color: '#8B5CF6', orden: 2 },
  { id: 'crecimiento', nombre: 'Crecimiento', descripcion: 'Las ventas empiezan a fluir, hay tracción. Necesita escalar y optimizar.', color: '#10B981', orden: 3 },
  { id: 'escala', nombre: 'Escala', descripcion: 'El negocio está maduro en el canal, factura bien, necesita eficiencia y expansión.', color: '#F59E0B', orden: 4 },
  { id: 'estancamiento', nombre: 'Estancamiento', descripcion: 'Las ventas se plancharon, no hay crecimiento. Algo está trabando el avance.', color: '#EF4444', orden: 5 },
  { id: 'retroceso', nombre: 'Retroceso', descripcion: 'Las ventas caen, hay pérdida de posicionamiento o relevancia en el canal.', color: '#DC2626', orden: 6 },
  { id: 'presion-estructural', nombre: 'Presión estructural', descripcion: 'Problemas internos del cliente (stock, logística, equipo, capital) limitan lo que se puede hacer.', color: '#F97316', orden: 7 },
  { id: 'movimiento-estrategico', nombre: 'Movimiento estratégico', descripcion: 'El cliente está haciendo un cambio grande: nuevo canal, nueva categoría, pivoteo de estrategia.', color: '#06B6D4', orden: 8 },
]

// ═══ Objetivos de Ciclo (qué queremos lograr) ═══
export const OBJETIVOS_TRABAJO_DEFAULT = [
  { id: 'ordenar-operacion', nombre: 'Ordenar operación', descripcion: 'Resolver problemas de stock, logística, abastecimiento y procesos internos.', icono: '📦', orden: 1 },
  { id: 'mejorar-rentabilidad', nombre: 'Mejorar rentabilidad', descripcion: 'Reducir costos operativos, optimizar márgenes, mejorar ROI del canal.', icono: '💰', orden: 2 },
  { id: 'crecer-ventas', nombre: 'Crecer en ventas', descripcion: 'Aumentar facturación, volumen de ventas y conversión.', icono: '📈', orden: 3 },
  { id: 'optimizar-catalogo', nombre: 'Optimizar catálogo', descripcion: 'Mejorar publicaciones, categorización, fotos, títulos y posicionamiento.', icono: '🛒', orden: 4 },
  { id: 'reducir-friccion', nombre: 'Reducir fricción', descripcion: 'Mejorar la relación consultora-cliente, alinear expectativas, resolver tensiones.', icono: '🤝', orden: 5 },
  { id: 'activar-canal', nombre: 'Activar canal', descripcion: 'Arrancar de cero en un canal nuevo: cuenta, publicaciones, primeras ventas.', icono: '🚀', orden: 6 },
  { id: 'destrabar-bloqueos', nombre: 'Destrabar bloqueos', descripcion: 'Resolver problemas internos del cliente que impiden avanzar: pagos, decisiones, recursos.', icono: '🔓', orden: 7 },
  { id: 'escalar-producto', nombre: 'Escalar producto estrella', descripcion: 'Identificar y empujar el producto que mejor funciona para maximizar su potencial.', icono: '⭐', orden: 8 },
  { id: 'profesionalizar', nombre: 'Profesionalizar gestión', descripcion: 'Mejorar procesos del cliente: facturación, métricas, herramientas, gestión de datos.', icono: '📊', orden: 9 },
  { id: 'estabilizar', nombre: 'Estabilizar operación', descripcion: 'Consolidar lo que funciona sin romper, mantener ritmo sin sobrecarga.', icono: '⚖️', orden: 10 },
]

// ───────────────────────────────────────────────────────────────────────────
// Integración Google Calendar + Meet (abril 2026)
// ───────────────────────────────────────────────────────────────────────────

// Estados del lifecycle de una reunión agendada (colección /meets).
export const MEET_ESTADOS = {
  AGENDADA:    'agendada',
  CANCELADA:   'cancelada',
  FINALIZADA:  'finalizada',   // marcada cuando pasó la hora final
  TRANSCRITA:  'transcrita',   // transcripción importada a /reuniones (Actividad)
}

export const MEET_ESTADO_LABELS = {
  agendada:   'Agendada',
  cancelada:  'Cancelada',
  finalizada: 'Finalizada',
  transcrita: 'Transcrita',
}

// Duraciones default que aparecen en el selector del modal
export const MEET_DURACIONES = [
  { min: 15,  label: '15 min' },
  { min: 30,  label: '30 min' },
  { min: 45,  label: '45 min' },
  { min: 60,  label: '1 h' },
  { min: 90,  label: '1 h 30' },
  { min: 120, label: '2 h' },
]

export const MEET_DURACION_DEFAULT = 30

// RSVP de invitados (mapeo 1-a-1 con Google Calendar responseStatus)
export const MEET_RSVP = {
  NEEDS_ACTION: 'needsAction',
  ACCEPTED:     'accepted',
  DECLINED:     'declined',
  TENTATIVE:    'tentative',
}

// Acciones del ABM /atajosChat
export const ATAJO_ACCIONES = {
  AGENDAR_REUNION:    'agendar_reunion',
  PROGRAMAR_MENSAJE:  'programar_mensaje',
  INSERTAR_SLA:       'insertar_sla',
  INSERTAR_PLANTILLA: 'insertar_plantilla',
  INVOCAR_IA:         'invocar_ia',
  INSERTAR_LINK:      'insertar_link',
}

export const ATAJO_ACCION_LABELS = {
  agendar_reunion:    'Agendar reunión',
  programar_mensaje:  'Programar mensaje',
  insertar_sla:       'Insertar SLA',
  insertar_plantilla: 'Insertar plantilla',
  invocar_ia:         'Invocar ClariA',
  insertar_link:      'Insertar link',
}

// ═══════════════════════════════════════════════════════════════════
// Módulo Rentabilidad — Constantes de Mercado Libre
// ═══════════════════════════════════════════════════════════════════

// Listing types activos en ML hoy. Los demás (gold, silver, bronze,
// gold_premium) están discontinuados desde 2023-08-05.
// Ver Developers_Costos por vender.html.
export const LISTING_TYPES_ML = {
  gold_pro:     { id: 'gold_pro',     nombre: 'Premium',  exposure: 'highest' },
  gold_special: { id: 'gold_special', nombre: 'Clásica',  exposure: 'highest' },
  free:         { id: 'free',         nombre: 'Gratuita', exposure: 'lowest' },
}

// Logistic types oficiales de ML. Cada uno con su shipping_mode asociado.
export const LOGISTIC_TYPES_ML = {
  drop_off:      { id: 'drop_off',      nombre: 'Mercado Envíos Drop Off', mode: 'me2' },
  cross_docking: { id: 'cross_docking', nombre: 'Mercado Envíos Colecta',  mode: 'me2' },
  xd_drop_off:   { id: 'xd_drop_off',   nombre: 'Mercado Envíos Places',   mode: 'me2' },
  self_service:  { id: 'self_service',  nombre: 'Mercado Envíos Flex',     mode: 'me2' },
  turbo:         { id: 'turbo',         nombre: 'Mercado Envíos Turbo',    mode: 'me2' },
  fulfillment:   { id: 'fulfillment',   nombre: 'Mercado Envíos Full',     mode: 'me2' },
  default:       { id: 'default',       nombre: 'Logística por defecto',   mode: 'me1' },
  custom:        { id: 'custom',        nombre: 'Personalizado',           mode: 'custom' },
  not_specified: { id: 'not_specified', nombre: 'No especificado',         mode: 'not_specified' },
}

// Tags de campañas de cuotas en MLA. Afectan `financing_add_on_fee` en /listing_prices.
// Solo MLA (en otros sites ML no aplica).
export const TAGS_CUOTAS_MLA = ['ahora-3', 'ahora-6', 'ahora-12', 'ahora-18']

// Variantes de campaña capturadas en cada doc del catálogo de costos por vender.
// Cada variante = 1 call al endpoint /listing_prices con esos tags.
// 'sin-campaña' es la base (call sin tags).
export const VARIANTES_CAMPAÑA_MLA = [
  { id: 'sin-campaña',    nombre: 'Sin campaña',         tags: null },
  { id: 'ahora-3',        nombre: 'Cuotas Ahora 3',      tags: 'ahora-3' },
  { id: 'ahora-6',        nombre: 'Cuotas Ahora 6',      tags: 'ahora-6' },
  { id: 'ahora-12',       nombre: 'Cuotas Ahora 12',     tags: 'ahora-12' },
  { id: 'ahora-18',       nombre: 'Cuotas Ahora 18',     tags: 'ahora-18' },
  { id: 'pcj-co-funded',  nombre: 'PCJ cofundado',       tags: 'pcj-co-funded' },
]

// ═══════════════════════════════════════════════════════════════════
// Sites de Mercado Libre — TABLA CONFIG-PAÍS (ADR 050, Fase D multipaís)
// ═══════════════════════════════════════════════════════════════════
// ÚNICA fuente de verdad ESTÁTICA por site. Los valores dinámicos (fee
// tables, tarifas Full, zonas) viven en los catálogos Firestore clavados
// por siteId — acá van solo capabilities y datos de referencia.
//
// ⚠️ ESPEJO en functions/sitesML.js (CJS — functions no puede importar este
// ESM). El test de paridad sitesML.parity.test.js falla si divergen.
// Cambiaste algo acá → cambialo allá.
//
// Campos (respuestas doc oficial ML, 18-jul-2026 — docs/PLAN_multipais-fase-d.md §1):
//   habilitado       — solo los 6 del alcance (AR/CL/UY/CO/PE/MX; Brasil
//                      EXCLUIDO, decisión Mariano 18-jul). MLB/MLQ/MLV quedan
//                      como referencia pero NUNCA elegibles.
//   decimales        — de /currencies (CLP y COP = 0; el resto 2).
//   authDomain       — dominio de auth OAuth por país (misma app/client_id).
//   flexDisponible   — Flex existe en los 6 (doc envios-flex).
//   fullDisponible   — Full solo AR/MX/CL/CO. NO existe en UY ni PE.
//   zipApiDisponible — ML no brinda códigos postales por API para CL/PE.
//   feeStructure     — 'nueva' = rollout fixed_fee 2026 (AR 12-03, CO 23-03,
//                      CL 06-04, MX 08-04); 'clasica' = MLU/MPE hasta probe.
//   umbralTH         — envío gratis obligatorio. Solo AR verificado ($33.000,
//                      abril 2026). Resto null-hasta-probe: la UI degrada a
//                      "sin datos", JAMÁS muestra el valor AR (ADR 050 §2).
export const SITES_ML = {
  MLA: {
    id: 'MLA', paisId: 'AR', moneda: 'ARS', nombre: 'Argentina',
    habilitado: true, decimales: 2, locale: 'es-AR',
    authDomain: 'auth.mercadolibre.com.ar',
    flexDisponible: true, fullDisponible: true, zipApiDisponible: true,
    feeStructure: 'nueva', requiereBillableWeight: true, umbralTH: 33000,
  },
  MLC: {
    id: 'MLC', paisId: 'CL', moneda: 'CLP', nombre: 'Chile',
    habilitado: true, decimales: 0, locale: 'es-CL',
    authDomain: 'auth.mercadolibre.cl',
    flexDisponible: true, fullDisponible: true, zipApiDisponible: false,
    // umbralTH: D7 probe real 19-jul-2026 ($19.989→fijo $1.000, $19.990→$0)
    feeStructure: 'nueva', requiereBillableWeight: false, umbralTH: 19990,
  },
  MLU: {
    id: 'MLU', paisId: 'UY', moneda: 'UYU', nombre: 'Uruguay',
    habilitado: true, decimales: 2, locale: 'es-UY',
    authDomain: 'auth.mercadolibre.com.uy',
    flexDisponible: true, fullDisponible: false, zipApiDisponible: true,
    // umbralTH: D7 probe real 19-jul-2026 ($999→fijo $40, $1.000→$0)
    feeStructure: 'clasica', requiereBillableWeight: false, umbralTH: 1000,
  },
  MCO: {
    id: 'MCO', paisId: 'CO', moneda: 'COP', nombre: 'Colombia',
    habilitado: true, decimales: 0, locale: 'es-CO',
    authDomain: 'auth.mercadolibre.com.co',
    flexDisponible: true, fullDisponible: true, zipApiDisponible: true,
    // umbralTH: D7 probe real 19-jul-2026 ($59.999→fijo $4.100, $60.000→$0)
    feeStructure: 'nueva', requiereBillableWeight: false, umbralTH: 60000,
  },
  MPE: {
    id: 'MPE', paisId: 'PE', moneda: 'PEN', nombre: 'Perú',
    habilitado: true, decimales: 2, locale: 'es-PE',
    authDomain: 'auth.mercadolibre.com.pe',
    flexDisponible: true, fullDisponible: false, zipApiDisponible: false,
    // umbralTH: D7 probe real 19-jul-2026 (S/38→fijo S/2, S/39→S/0)
    feeStructure: 'clasica', requiereBillableWeight: false, umbralTH: 39,
  },
  MLM: {
    id: 'MLM', paisId: 'MX', moneda: 'MXN', nombre: 'México',
    habilitado: true, decimales: 2, locale: 'es-MX',
    authDomain: 'auth.mercadolibre.com.mx',
    flexDisponible: true, fullDisponible: true, zipApiDisponible: true,
    // umbralTH: D7 probe real 19-jul-2026 ($298→fijo $37, $299→$0)
    feeStructure: 'nueva', requiereBillableWeight: false, umbralTH: 299,
  },
  // ── NO habilitados (referencia; jamás elegibles) ──
  MLB: { id: 'MLB', paisId: 'BR', moneda: 'BRL', nombre: 'Brasil',    habilitado: false },
  MLQ: { id: 'MLQ', paisId: 'EC', moneda: 'USD', nombre: 'Ecuador',   habilitado: false },
  MLV: { id: 'MLV', paisId: 'VE', moneda: 'VES', nombre: 'Venezuela', habilitado: false },
}

// Sites elegibles (los 6 del alcance Fase D). Congela el orden para UI.
export const SITES_ML_HABILITADOS = ['MLA', 'MLC', 'MLU', 'MCO', 'MPE', 'MLM']

/**
 * Config de un site — FAIL-LOUD (ADR 050): un siteId desconocido o no
 * habilitado es un error de programa, nunca un fallback silencioso a MLA.
 * @param {string} siteId
 * @param {{permitirNoHabilitado?: boolean}} [opts] — para lecturas de datos
 *        históricos de un site deshabilitado (no para elegir/conectar).
 */
export function getSiteML(siteId, { permitirNoHabilitado = false } = {}) {
  const site = SITES_ML[siteId]
  if (!site) throw new Error(`getSiteML: siteId desconocido "${siteId}"`)
  if (!site.habilitado && !permitirNoHabilitado) {
    throw new Error(`getSiteML: site "${siteId}" no habilitado (alcance Fase D: ${SITES_ML_HABILITADOS.join(', ')})`)
  }
  return site
}
/**
 * Moneda HOGAR del site inferida del prefijo del item (MLA123→ARS, MLC9→CLP).
 * D4a-2 (ADR 050 §10): el costo va en moneda hogar del site, no la de venta.
 * ⚠️ ESPEJO de functions/sitesML.js — test de paridad.
 */
export function monedaPorPrefijoItem(itemId, fallback = 'ARS') {
  for (const s of SITES_ML_HABILITADOS) {
    if (String(itemId || '').startsWith(s)) return SITES_ML[s].moneda
  }
  return fallback
}


// ═══════════════════════════════════════════════════════════════════
// Envíos MLA — constantes de zonas, tramos, modalidades
// ═══════════════════════════════════════════════════════════════════

// Zonas de AR para capturar tarifas. Cada una se representa con un state_id
// de ML al hacer las llamadas a shipping_options/free.
export const ZONAS_AR = ['CABA', 'GBA', 'NOA', 'NEA', 'Patagonia']

export const ZONAS_AR_A_STATE_ID = {
  CABA:      { stateId: 'AR-C', label: 'Ciudad de Buenos Aires' },
  GBA:       { stateId: 'AR-B', label: 'Buenos Aires (GBA)' },
  NOA:       { stateId: 'AR-T', label: 'Tucumán (representativa NOA)' },
  NEA:       { stateId: 'AR-W', label: 'Corrientes (representativa NEA)' },
  Patagonia: { stateId: 'AR-U', label: 'Chubut (representativa Patagonia)' },
}

// Tramos de peso FACTURABLE (no peso real — incluye volumétrico).
// ML calcula peso_facturable = max(peso_real, L×W×H/5000).
export const TRAMOS_PESO_MLA = [
  { id: '0-500g',    desde: 0,     hasta: 500,   label: '0 - 500 g' },
  { id: '501-1000g', desde: 501,   hasta: 1000,  label: '501 g - 1 kg' },
  { id: '1-2kg',     desde: 1001,  hasta: 2000,  label: '1 - 2 kg' },
  { id: '2-5kg',     desde: 2001,  hasta: 5000,  label: '2 - 5 kg' },
  { id: '5-10kg',    desde: 5001,  hasta: 10000, label: '5 - 10 kg' },
  { id: '10-25kg',   desde: 10001, hasta: 25000, label: '10 - 25 kg' },
]

// Tramos de precio para fixed_fee (costo adicional por producto).
// ML no lo cobra arriba de ~$30.000.
export const TRAMOS_PRECIO_FIXED_FEE_MLA = [
  { precioHasta: 9999,  label: 'hasta $9.999' },
  { precioHasta: 16999, label: '$10.000 - $16.999' },
  { precioHasta: 29999, label: '$17.000 - $29.999' },
  { precioHasta: null,  label: '$30.000 o más' },
]

// Modalidades de envío ML (mapeo logistic_type → catálogo)
export const MODALIDADES_ENVIO_MLA = {
  // ME2 — Mercado Envíos 2 (dinámico). 3 sub-tipos con mismo tarifario base.
  drop_off:      { catalogo: 'catalogoEnvioME2MLA',   grupo: 'me2',  nombre: 'Drop Off',        icono: '📦', subTipo: 'drop_off',      mode: 'me2' },
  cross_docking: { catalogo: 'catalogoEnvioME2MLA',   grupo: 'me2',  nombre: 'Cross Docking',   icono: '📦', subTipo: 'cross_docking', mode: 'me2' },
  xd_drop_off:   { catalogo: 'catalogoEnvioME2MLA',   grupo: 'me2',  nombre: 'Places',          icono: '📦', subTipo: 'xd_drop_off',   mode: 'me2' },
  // Flex — self_service
  self_service:  { catalogo: 'catalogoEnvioFlexMLA',  grupo: 'flex', nombre: 'Flex',            icono: '⚡', mode: 'me2' },
  // Full — fulfillment (con inspección por categoría)
  fulfillment:   { catalogo: 'catalogoEnvioFullMLA',  grupo: 'full', nombre: 'Full',            icono: '🏭', mode: 'me2' },
  // Próximamente (growth on-demand)
  turbo:         { catalogo: 'catalogoEnvioTurboMLA', grupo: 'turbo', nombre: 'Turbo',          icono: '🚀', mode: 'me2' },
  default:       { catalogo: 'catalogoEnvioME1MLA',   grupo: 'me1',  nombre: 'ME1 (default)',   icono: '📮', mode: 'me1' },
  custom:        { catalogo: 'catalogoEnvioCustomMLA', grupo: 'custom', nombre: 'Custom',       icono: '🔧', mode: 'custom' },
}

// Catálogos de envíos disponibles — registro para la UI
export const CATALOGOS_ENVIOS_MLA = [
  { id: 'me2',    coleccion: 'catalogoEnvioME2MLA',   nombre: 'Drop Off / Colecta / Places (ME2)', icono: '📦', estado: 'activo',       orden: 1 },
  { id: 'flex',   coleccion: 'catalogoFlexMLA',       nombre: 'Mercado Envíos Flex',                icono: '⚡', estado: 'activo',       orden: 2 },
  { id: 'full',   coleccion: 'catalogoFullMLA',       nombre: 'Mercado Envíos Full',                icono: '🏭', estado: 'activo',       orden: 3 },
  { id: 'turbo',  coleccion: 'catalogoEnvioTurboMLA', nombre: 'Mercado Envíos Turbo',               icono: '🚀', estado: 'proximamente', orden: 4 },
  { id: 'me1',    coleccion: 'catalogoEnvioME1MLA',   nombre: 'ME1 (Logística propia)',             icono: '📮', estado: 'proximamente', orden: 5 },
  { id: 'custom', coleccion: 'catalogoEnvioCustomMLA', nombre: 'Custom (flete propio)',             icono: '🔧', estado: 'proximamente', orden: 6 },
]

// Umbral TH default de envío gratis obligatorio para AR (abril 2026).
// Editable desde UI por modalidad.
// ADR 050: fuente canónica = SITES_ML.MLA.umbralTH (era copia literal).
export const UMBRAL_TH_ENVIO_GRATIS_MLA_DEFAULT = SITES_ML.MLA.umbralTH

// ═══════════════════════════════════════════════════════════════════
// Registro central de Catálogos de Costos por Vender
// ═══════════════════════════════════════════════════════════════════
// Cada plataforma × país es su propia colección Firestore.
// Agregar una entrada nueva acá cuando se sume una plataforma o un país.
// Los helpers genéricos (ver src/core/lib/catalogoCostosPorVender.js)
// reciben el `id` del catálogo y resuelven la colección correcta desde este registro.
// D5a (ADR 050 §9): los 6 sites habilitados, generados desde SITES_ML —
// una entrada por site, jamás literales duplicados. Campos por site:
//   - coleccion: colección-por-site (ADR 050 §2) — catalogoCostosPorVender{SITE}
//   - tagsCuotasDisponibles: los tags de cuotas (ahora-X) son solo-MLA (doc ML)
//   - umbralTHDefault: fuente canónica SITES_ML[site].umbralTH — null-hasta-probe
//     fuera de AR, JAMÁS heredar el valor AR
//   - disponible/motivo: contrato de degradación — un catálogo sin datos reales
//     se declara NO disponible y la UI lo excluye por default (D7 lo habilita)
export const CATALOGOS_COSTOS_POR_VENDER = Object.fromEntries(
  SITES_ML_HABILITADOS.map((siteId) => {
    const site = SITES_ML[siteId]
    const esMLA = siteId === 'MLA'
    return [siteId, {
      id: siteId,
      coleccion: `catalogoCostosPorVender${siteId}`,
      plataformaId: 'mercado-libre',
      siteId: site.id,
      paisId: site.paisId,
      moneda: site.moneda,
      nombre: `Mercado Libre ${site.nombre}`,
      fuente: 'api',
      // Dato del registro: link informativo en UI (TablaCostosPorVender) y
      // query del simulador vía consultarListingPricesLive (endpoint PÚBLICO,
      // sin token, desde el browser — la infra safeFetch es de functions y el
      // fetch backend equivalente SÍ pasa por mlSafeFetch, ver costosPorVender).
      endpointBase: `https://api.mercadolibre.com/sites/${siteId}/listing_prices`, // @externalApi GET /sites/{site}/listing_prices (dato, no fetch)
      requiereBillableWeight: site.requiereBillableWeight,  // obligatorio solo en MLA (2026)
      tagsCuotasDisponibles: esMLA ? ['ahora-3', 'ahora-6', 'ahora-12', 'ahora-18'] : [],
      // Umbral envío gratis: no expuesto por API. Fuente oficial MLA:
      // https://www.mercadolibre.com.ar/ayuda/como-funciona-beneficio-envios-gratis_4603
      umbralTHDefault: site.umbralTH,
      // D5a (ADR 050 §2): contrato de degradación explícita — un catálogo se
      // enciende recién cuando SU colección tiene datos reales.
      // D7 CIERRE (20-jul-2026): seeds corridos y verificados en staging para
      // los 6 sites (~3.2-3.5K categorías vigentes c/u + fixedFee v1 con TH y
      // estructura del probe empírico 19-jul). Sites futuros nacen en false.
      // NOTA prod: al promover, correr los seeds en prod ANTES del rollout
      // (checklist pre-prod) — el registro es build-time, no por entorno.
      disponible: true,
      motivo: null,
    }]
  })
)

// El rol «Suspendido» tal como lo crean `resolverRolSuspendidoId` (estadoEmpresa.js) y
// `seedRolesDefault` (seedRoles.js): con id fijo `ROL_SUSPENDIDO_CODIGO`, como los
// roles que crea RolesPage, y con estos valores. Son los únicos que `firestore.rules`
// le deja escribir al staff (`VALORES_ROL_SUSPENDIDO`, report S0PfTXHuHFEVKHlu3Nn6):
// cambiar uno acá sin cambiarlo allá lo pone rojo
// `functions/tests/integration/firestoreRulesUsuariosContencion.integration.test.js`.
export const ROL_SUSPENDIDO = {
  codigo: ROL_SUSPENDIDO_CODIGO,
  nombre: 'Suspendido',
  color: '#DC2626',
  orden: 999,
  activo: true,
  aplicaA: ['cliente', 'colaborador'],
  esSistema: true,                    // no se puede borrar desde UI
  descripcion: 'Rol temporal automático para usuarios de empresas suspendidas. Solo permite ver, no operar.',
  permisos: [],
}
